/**
 * PATH: apps/quote/src/components/builder/sheets/terms-sheet/RichTextEditor.tsx
 * PURPOSE: Simple contentEditable rich text editor with toolbar
 */

import { useState, useCallback, useRef, useEffect } from 'react'
import { Bold, Italic, List, ListOrdered, Heading1, Heading2, Heading3, Undo, Redo } from 'lucide-react'
import { cn } from '@/lib/utils'

interface RichTextEditorProps {
  value: string
  onChange: (html: string) => void
  placeholder?: string
  readOnly?: boolean
}

export function RichTextEditor({ value, onChange, placeholder = 'Enter terms and conditions...', readOnly = false }: RichTextEditorProps) {
  const editorRef = useRef<HTMLDivElement>(null)
  const [isEmpty, setIsEmpty] = useState(!value)

  useEffect(() => {
    if (editorRef.current && value !== editorRef.current.innerHTML) {
      editorRef.current.innerHTML = value || ''
      setIsEmpty(!value)
    }
  }, [value])

  const handleInput = useCallback(() => {
    if (editorRef.current) {
      const html = editorRef.current.innerHTML
      setIsEmpty(!html || html === '<br>' || html === '<p><br></p>')
      onChange(html)
    }
  }, [onChange])

  const execCommand = useCallback((command: string, val?: string) => {
    document.execCommand(command, false, val)
    editorRef.current?.focus()
    handleInput()
  }, [handleInput])

  const toolbar = [
    { icon: Bold, command: 'bold', title: 'Bold (Ctrl+B)' },
    { icon: Italic, command: 'italic', title: 'Italic (Ctrl+I)' },
    { divider: true },
    { icon: Heading1, command: 'formatBlock', value: 'h3', title: 'Heading 1' },
    { icon: Heading2, command: 'formatBlock', value: 'h4', title: 'Heading 2' },
    { icon: Heading3, command: 'formatBlock', value: 'h5', title: 'Heading 3' },
    { divider: true },
    { icon: List, command: 'insertUnorderedList', title: 'Bullet List' },
    { icon: ListOrdered, command: 'insertOrderedList', title: 'Numbered List' },
    { divider: true },
    { icon: Undo, command: 'undo', title: 'Undo (Ctrl+Z)' },
    { icon: Redo, command: 'redo', title: 'Redo (Ctrl+Y)' },
  ]

  return (
    <div className="border border-slate-200 dark:border-slate-700 rounded-lg overflow-hidden bg-white dark:bg-slate-900">
      {!readOnly && (
        <div className="flex items-center gap-1 px-2 py-1.5 border-b border-slate-200 dark:border-slate-700 bg-slate-50 dark:bg-slate-800/50">
          {toolbar.map((item, i) =>
            item.divider ? (
              <div key={i} className="w-px h-5 bg-slate-200 dark:bg-slate-700 mx-1" />
            ) : (
              <button key={i} type="button" onClick={() => execCommand(item.command!, item.value)} title={item.title} className="p-1.5 rounded hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-600 dark:text-slate-400 transition-colors">
                <item.icon className="w-4 h-4" />
              </button>
            )
          )}
        </div>
      )}
      <div className="relative">
        {isEmpty && !readOnly && (
          <div className="absolute inset-0 px-4 py-3 text-slate-400 pointer-events-none">{placeholder}</div>
        )}
        <div
          ref={editorRef}
          contentEditable={!readOnly}
          onInput={handleInput}
          className={cn(
            'min-h-[300px] max-h-[500px] overflow-y-auto px-4 py-3 text-sm focus:outline-none',
            'prose prose-sm dark:prose-invert max-w-none',
            '[&_h3]:text-base [&_h3]:font-semibold [&_h3]:mt-4 [&_h3]:mb-2',
            '[&_h4]:text-sm [&_h4]:font-semibold [&_h4]:mt-3 [&_h4]:mb-2',
            '[&_h5]:text-sm [&_h5]:font-medium [&_h5]:mt-2 [&_h5]:mb-1',
            '[&_p]:mb-2 [&_ul]:list-disc [&_ul]:pl-5 [&_ul]:mb-2',
            '[&_ol]:list-decimal [&_ol]:pl-5 [&_ol]:mb-2 [&_li]:mb-1',
            readOnly && 'cursor-default'
          )}
        />
      </div>
    </div>
  )
}
